import { StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import type { OpsDomainListRow } from '@/api/operations-resources';
import { formatDate } from '@/lib/format';
import { EntityLink } from '@/components/entity-link';

type Tone = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'neutral';

/** Domain status → StatusPill tone (models/domain.go statuses). */
const STATUS_TONE: Record<string, Tone> = {
  active: 'success',
  pending: 'warning',
  expired: 'danger',
  terminated: 'muted',
};

/**
 * DataTable columns for the cross-org Domains inventory. The Organization cell
 * links to the org detail; the row itself is not clickable in Phase 1.
 */
export const opsDomainColumns: ColumnDef<OpsDomainListRow, unknown>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Domain',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-default)', fontWeight: 'var(--vn-font-weight-medium)' }}>
        {row.original.name || '—'}
      </span>
    ),
  },
  {
    id: 'org',
    accessorKey: 'organization_id',
    header: 'Customer',
    cell: ({ row }) => {
      const { organization_id, customer } = row.original;
      return (
        <EntityLink
          to={`/operations/orgs/${organization_id}`}
          id={organization_id}
          name={customer?.customer_company_name || customer?.customer_name}
        />
      );
    },
  },
  {
    id: 'provider',
    accessorKey: 'provider',
    header: 'Provider',
    cell: ({ row }) => (
      <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.provider || '—'}</span>
    ),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status || 'active';
      const tone = STATUS_TONE[status] ?? 'neutral';
      return (
        <StatusPill tone={tone} tooltip={status}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </StatusPill>
      );
    },
  },
  {
    id: 'expired_at',
    accessorKey: 'expired_at',
    header: 'Expires',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {row.original.expired_at ? formatDate(row.original.expired_at) : '—'}
      </span>
    ),
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
];

/** Column ids toggleable via Display options. */
export const OPS_DOMAIN_TOGGLEABLE_COLUMN_IDS = ['provider', 'expired_at', 'created_at'];
